import React, { useState, useEffect, useRef } from 'react';
import { useTimelineContext } from '../context/TimelineContext';
import { useTutorialContext } from '../context/TutorialContext';
import {
  ERAS,
  compareHistoricalDates,
  dateToHistoricalDate,
  historicalDateToDate,
  validateHistoricalDate
} from '../utils/historicalDate';
import './EventForm.css';
import TagSelector from './TagSelector';

const getTodayParts = () => dateToHistoricalDate(new Date());

const getDatePartsForForm = (dateParts) => {
  return dateParts && typeof dateParts === 'object' ? dateParts : getTodayParts();
};

const cloneDateParts = (dateParts) => ({ ...getDatePartsForForm(dateParts) });
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const formatHistoricalDatePreview = (dateParts) => {
  const validation = validateHistoricalDate(dateParts);
  if (!validation.isValid) return '';

  const month = Number(dateParts.month);
  return `${MONTH_NAMES[month - 1]} ${Number(dateParts.day)}, ${Number(dateParts.year)} ${dateParts.era}`;
};

const getDateSummary = (dateParts, fallback = 'Enter a valid date') => {
  return formatHistoricalDatePreview(dateParts) || fallback;
};

const getEventImageUrl = (event) => {
  return event?.imageUrl || event?.imageURL || event?.image || '';
};

const DateFields = ({ idPrefix, label, value, onChange, error, hideLegend = false }) => {
  const currentValue = getDatePartsForForm(value);
  const errorId = `${idPrefix}-error`;
  const updatePart = (part, partValue) => {
    if (part === 'year' && typeof partValue === 'string' && partValue.trim().startsWith('-')) {
      onChange({
        ...currentValue,
        era: ERAS.BCE,
        year: partValue.trim().replace(/^-+/, '')
      });
      return;
    }

    onChange({
      ...currentValue,
      [part]: partValue
    });
  };
  const selectDateInputText = (event) => {
    event.target.select();
  };

  return (
    <fieldset className="historical-date-group">
      <legend className={hideLegend ? 'visually-hidden' : undefined}>{label}</legend>
      <div className="historical-date-fields">
        <div className="date-part era-part">
          <label htmlFor={`${idPrefix}-era`}>Era</label>
          <select
            id={`${idPrefix}-era`}
            value={currentValue.era}
            onChange={e => updatePart('era', e.target.value)}
          >
            <option value={ERAS.CE}>CE</option>
            <option value={ERAS.BCE}>BCE</option>
          </select>
        </div>
        <div className="date-part year-part">
          <label htmlFor={`${idPrefix}-year`}>Year</label>
          <input
            id={`${idPrefix}-year`}
            type="number"
            min="1"
            step="1"
            value={currentValue.year}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            onFocus={selectDateInputText}
            onClick={selectDateInputText}
            onChange={e => updatePart('year', e.target.value)}
            required
          />
        </div>
        <div className="date-part month-part">
          <label htmlFor={`${idPrefix}-month`}>Month</label>
          <input
            id={`${idPrefix}-month`}
            type="number"
            min="1"
            max="12"
            step="1"
            value={currentValue.month}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            onFocus={selectDateInputText}
            onClick={selectDateInputText}
            onChange={e => updatePart('month', e.target.value)}
            required
          />
        </div>
        <div className="date-part day-part">
          <label htmlFor={`${idPrefix}-day`}>Day</label>
          <input
            id={`${idPrefix}-day`}
            type="number"
            min="1"
            max="31"
            step="1"
            value={currentValue.day}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            onFocus={selectDateInputText}
            onClick={selectDateInputText}
            onChange={e => updatePart('day', e.target.value)}
            required
          />
        </div>
      </div>
      {error && <p id={errorId} className="form-error">{error}</p>}
    </fieldset>
  );
};

const areValidHistoricalDates = (startDate, endDate) => {
  return validateHistoricalDate(startDate).isValid && validateHistoricalDate(endDate).isValid;
};

const EventForm = ({ onSave, onCancel, onDelete }) => {
  const { 
    editingEvent, 
    addEvent, 
    updateEvent, 
    deleteEvent,
    setEditingEvent,
    allTags
  } = useTimelineContext();
  const { beginnerStepId, isBeginnerTutorialActive } = useTutorialContext();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',
    date: getTodayParts(),
    endDate: '',
    position: 'top',
    tags: [],
    type: 'point'
  });
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [dateErrors, setDateErrors] = useState({});
  const [titleError, setTitleError] = useState('');
  const initialTutorialDateRef = useRef(null);
  const isEditingExistingEvent = Boolean(editingEvent?.id);
  const requiresTutorialDateChange = isBeginnerTutorialActive && beginnerStepId === 'event-form' && !isEditingExistingEvent;
  const disableRangeCreationForTutorial = requiresTutorialDateChange;
  const isRangeEvent = formData.type === 'range';
  const canCompareRangeDates = isRangeEvent && areValidHistoricalDates(formData.date, formData.endDate);
  const isRangeReversed = canCompareRangeDates && compareHistoricalDates(formData.endDate, formData.date) < 0;
  const startDateSummary = dateErrors.date
    ? 'Enter a valid start date.'
    : getDateSummary(formData.date);
  const pointDateSummary = dateErrors.date
    ? dateErrors.date.startsWith('Change the date')
      ? getDateSummary(formData.date)
      : 'Enter a valid date.'
    : getDateSummary(formData.date);
  const endDateSummary = dateErrors.endDate
    ? 'Enter a valid end date.'
    : isRangeEvent
    ? getDateSummary(formData.endDate)
    : '';
  const dateSectionSummary = isRangeEvent
    ? isRangeReversed
      ? 'End date is before start date.'
      : `${startDateSummary} - ${endDateSummary}`
    : pointDateSummary;
  const hasOptionalDetails = Boolean(formData.imageUrl || formData.tags.length);

  useEffect(() => {
    if (editingEvent) {
      setIsConfirmingDelete(false);
      setDateErrors({});
      setTitleError('');
      setFormData({
        ...editingEvent,
        date: dateToHistoricalDate(editingEvent.date),
        endDate: editingEvent.endDate ? dateToHistoricalDate(editingEvent.endDate) : '',
        type: editingEvent.type || 'point',
        tags: editingEvent.tags || [],
        imageUrl: getEventImageUrl(editingEvent)
      });
      initialTutorialDateRef.current = (
        isBeginnerTutorialActive
        && beginnerStepId === 'event-form'
        && !editingEvent.id
      )
        ? dateToHistoricalDate(editingEvent.date)
        : null;
    }
  }, [beginnerStepId, editingEvent, isBeginnerTutorialActive]);

  useEffect(() => {
    if (!requiresTutorialDateChange) {
      initialTutorialDateRef.current = null;
      return;
    }

    if (editingEvent) return;

    if (!initialTutorialDateRef.current) {
      initialTutorialDateRef.current = cloneDateParts(formData.date);
    }
  }, [editingEvent, formData.date, requiresTutorialDateChange]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextTitleError = formData.title.trim() ? '' : 'Title is required.';
    const startValidation = validateHistoricalDate(formData.date);
    const endValidation = formData.type === 'range'
      ? validateHistoricalDate(formData.endDate)
      : { isValid: true, errors: [] };
    const nextDateErrors = {
      date: startValidation.errors.join(' '),
      endDate: endValidation.errors.join(' ')
    };

    if (
      startValidation.isValid &&
      endValidation.isValid &&
      formData.type === 'range' &&
      compareHistoricalDates(formData.endDate, formData.date) < 0
    ) {
      nextDateErrors.endDate = 'End date must be after the start date.';
    }

    if (
      requiresTutorialDateChange &&
      startValidation.isValid &&
      initialTutorialDateRef.current &&
      compareHistoricalDates(formData.date, initialTutorialDateRef.current) === 0
    ) {
      nextDateErrors.date = 'Change the date before creating this tutorial event.';
    }

    setTitleError(nextTitleError);
    setDateErrors(nextDateErrors);

    if (nextTitleError || nextDateErrors.date || nextDateErrors.endDate) {
      return;
    }
    
    const eventData = {
      ...formData,
      id: editingEvent?.id || Date.now(),
      title: formData.title.trim(),
      date: historicalDateToDate(formData.date),
      endDate: formData.type === 'range' ? historicalDateToDate(formData.endDate) : null,
      type: formData.type,
      position: formData.type === 'range' ? 'bottom' : formData.position,
      imageUrl: formData.imageUrl.trim()
    };

    if (!eventData.imageUrl) {
      delete eventData.imageUrl;
    }

    if (isEditingExistingEvent) {
      updateEvent(eventData);
    } else {
      addEvent(eventData);
    }
    setEditingEvent(null);
    onSave?.();
  };

  const handleCancel = () => {
    setEditingEvent(null);
    setDateErrors({});
    setTitleError('');
    setFormData({ title: '', description: '', imageUrl: '', date: getTodayParts(), endDate: '', tags: [], position: 'top', type: 'point' });
    onCancel?.();
  };

  const handleDelete = () => {
    if (editingEvent) {
      deleteEvent(editingEvent.id);
      setIsConfirmingDelete(false);
      onDelete?.();
    }
  };

  const handleTagChange = (tags) => {
    setFormData(prev => ({
      ...prev,
      tags
    }));
  };

  const addEndDate = () => {
    setDateErrors({});
    setFormData(prev => ({
      ...prev,
      type: 'range',
      endDate: cloneDateParts(prev.endDate || prev.date)
    }));
  };

  const removeEndDate = () => {
    setDateErrors(prev => ({ ...prev, endDate: '' }));
    setFormData(prev => ({
      ...prev,
      type: 'point',
      endDate: ''
    }));
  };

  const copyStartDateToEndDate = () => {
    setDateErrors(prev => ({ ...prev, endDate: '' }));
    setFormData(prev => ({
      ...prev,
      endDate: cloneDateParts(prev.date)
    }));
  };

  const swapRangeDates = () => {
    setDateErrors({});
    setFormData(prev => ({
      ...prev,
      date: cloneDateParts(prev.endDate),
      endDate: cloneDateParts(prev.date)
    }));
  };

  return (
    <div className="event-form-overlay">
      <div className="event-form">
        <h2>{isEditingExistingEvent ? 'Edit Event' : 'Create Event'}</h2>
        <form onSubmit={handleSubmit} className="event-form-fields" noValidate>
          <div className="tutorial-form-core" data-tutorial-id="event-form-core">
          <div className="form-group">
            <label htmlFor="event-title">Title</label>
            <input
              id="event-title"
              type="text"
              value={formData.title}
              aria-invalid={Boolean(titleError)}
              aria-describedby={titleError ? 'event-title-error' : undefined}
              onChange={e => {
                setTitleError('');
                setFormData({...formData, title: e.target.value});
              }}
              required
            />
            {titleError && <p id="event-title-error" className="form-error">{titleError}</p>}
          </div>

          {isRangeEvent ? (
            <div className="form-group">
              <div className="date-range-panel">
                <div className="date-range-heading">
                  <div>
                    <span>Date range</span>
                    <p className="date-summary" aria-live="polite">{dateSectionSummary}</p>
                  </div>
                  <button
                    type="button"
                    className="secondary-button range-helper-button"
                    onClick={copyStartDateToEndDate}
                  >
                    End same as start
                  </button>
                </div>

                <DateFields
                  idPrefix="event-date"
                  label="Start Date"
                  value={formData.date}
                  onChange={date => {
                    setDateErrors(prev => ({ ...prev, date: '' }));
                    setFormData({...formData, date});
                  }}
                  error={dateErrors.date}
                />

                <div className="range-date-divider" aria-hidden="true" />

                <DateFields
                  idPrefix="event-end-date"
                  label="End Date"
                  value={formData.endDate}
                  onChange={endDate => {
                    setDateErrors(prev => ({ ...prev, endDate: '' }));
                    setFormData({...formData, endDate});
                  }}
                  error={dateErrors.endDate}
                />

                {isRangeReversed && (
                  <button
                    type="button"
                    className="secondary-button range-swap-button"
                    onClick={swapRangeDates}
                  >
                    Swap start and end
                  </button>
                )}

                <button
                  type="button"
                  className="secondary-button range-remove-button"
                  onClick={removeEndDate}
                >
                  Remove end date
                </button>
              </div>
            </div>
          ) : (
            <div className="form-group compact-date-section">
              <div className="date-section-heading">
                <span>Date</span>
                <p className="date-summary" aria-live="polite">{dateSectionSummary}</p>
              </div>
              <DateFields
                idPrefix="event-date"
                label="Date"
                value={formData.date}
                onChange={date => {
                  setDateErrors(prev => ({ ...prev, date: '' }));
                  setFormData({...formData, date});
                }}
                error={dateErrors.date}
                hideLegend
              />
              <button
                type="button"
                className="secondary-button range-add-button"
                onClick={addEndDate}
                disabled={disableRangeCreationForTutorial}
                aria-describedby={disableRangeCreationForTutorial ? 'tutorial-range-helper' : undefined}
              >
                Add end date
              </button>
              {disableRangeCreationForTutorial && (
                <p id="tutorial-range-helper" className="tutorial-disabled-helper">
                  Ranges are covered in the advanced tutorial.
                </p>
              )}
            </div>
          )}
          </div>

          {!isRangeEvent && (
            <div className="form-group">
              <fieldset className="position-group">
                <legend>Position</legend>
                <div className="position-options">
                  <label className={`position-option ${formData.position === 'top' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="event-position"
                      value="top"
                      checked={formData.position === 'top'}
                      onChange={e => setFormData({...formData, position: e.target.value})}
                    />
                    <span className="position-icon top-icon" aria-hidden="true">
                      <span className="position-card" />
                      <span className="position-line" />
                    </span>
                    <span>Above</span>
                  </label>
                  <label className={`position-option ${formData.position === 'bottom' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="event-position"
                      value="bottom"
                      checked={formData.position === 'bottom'}
                      onChange={e => setFormData({...formData, position: e.target.value})}
                    />
                    <span className="position-icon bottom-icon" aria-hidden="true">
                      <span className="position-line" />
                      <span className="position-card" />
                    </span>
                    <span>Below</span>
                  </label>
                </div>
              </fieldset>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="event-description">Description</label>
            <textarea
              id="event-description"
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              placeholder="Event Description"
              rows={2}
            />
          </div>

          <details className="optional-event-details" open={isEditingExistingEvent && hasOptionalDetails}>
            <summary>Image and tags</summary>
            <div className="optional-event-details-content">
              <div className="form-group">
                <label htmlFor="event-image-url">Image URL</label>
                <input
                  id="event-image-url"
                  type="url"
                  value={formData.imageUrl}
                  onChange={e => setFormData({...formData, imageUrl: e.target.value})}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <TagSelector
                selectedTags={formData.tags}
                existingTags={allTags}
                onChange={handleTagChange}
              />
            </div>
          </details>

          <div className="form-actions primary-form-actions">
            <button type="submit" data-tutorial-id="event-form-submit">
              {isEditingExistingEvent ? 'Update' : 'Create'}
            </button>
            <button type="button" onClick={handleCancel} className="secondary-button">
              Cancel
            </button>
          </div>
          {isEditingExistingEvent && (
            isConfirmingDelete ? (
              <div className="delete-confirmation">
                <p>Delete this event?</p>
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => setIsConfirmingDelete(false)}
                    className="secondary-button"
                  >
                    Cancel Delete
                  </button>
                  <button type="button" onClick={handleDelete} className="delete-button">
                    Confirm Delete
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsConfirmingDelete(true)}
                className="delete-button"
              >
                Delete Event
              </button>
            )
          )}
        </form>
      </div>
    </div>
  );
};

export default EventForm; 
